import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// Config
import { urls, getRoute } from "../../config/routes";
import { capturePages } from "../../config/values";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import usePhysicalDamage from "../../hooks/usePhysicalDamage";
import useTranslate from "../../hooks/useTranslate";

// Actions
import {
  getPhysicalDamages,
  updatePhysicalDamage,
  uploadDescriptions,
  uploadEstimatedCost
} from "../../state/physicalDamage/actions";

// Components
import EditMediaForm from "../EditMediaForm";
import LoadingSection from "../LoadingSection";

const loadInitialValues = ({ descriptions, estimatedCosts }, isDraft) => ({
  descriptions: isDraft
    ? [descriptions[0] ? descriptions[0].text : ""]
    : [""],
  estimatedCosts: isDraft
    ? [estimatedCosts[0] ? estimatedCosts[0].text : ""]
    : [""]
});

const filterEmpty = arrayStrings => arrayStrings.filter(s => s && s !== "");
const toApiValue = value => (value.trim() === "" ? null : value);

export default () => {
  const { trackException } = useAppInsights();
  const dispatch = useDispatch();
  const getText = useTranslate();

  const { caseId, physicalDamageId } = useParams();
  const { data, imageHeaders, isDeletingImage, isLoading, loadingHeaders } =
    usePhysicalDamage(caseId, physicalDamageId);

  const {
    data: { isDraft }
  } = useSelector(state => state.caseDetail);

  // Same "Physical Damage N" title as PhysicalDamageDetailPanel.jsx - reuses
  // its list-position lookup instead of a dedicated edit-page title copy.
  const { list } = useSelector(state => state.physicalDamage);
  useEffect(() => {
    if (!list) dispatch(getPhysicalDamages({ caseId }));
  }, [caseId, dispatch, list]);
  const index = list
    ? list.findIndex(item => item.id === physicalDamageId)
    : -1;
  const displayNumber = index >= 0 ? index + 1 : "";

  if (isLoading || !data) return <LoadingSection />;

  const onSubmit = (values, { setSubmitting, resetForm, goBack }) => {
    const descriptions = filterEmpty(values.descriptions);
    const estimatedCosts = filterEmpty(values.estimatedCosts);
    if (isDraft) {
      const currentDescription =
        (data.descriptions[0] && data.descriptions[0].text) || "";
      const currentEstimatedCost =
        (data.estimatedCosts[0] && data.estimatedCosts[0].text) || "";
      const newDescription = values.descriptions[0] || "";
      const newEstimatedCost = values.estimatedCosts[0] || "";

      // Skip the PUT if unchanged
      if (
        newDescription.trim() === currentDescription.trim() &&
        newEstimatedCost.trim() === currentEstimatedCost.trim()
      ) {
        setSubmitting(false);
        goBack();
        return;
      }

      dispatch(
        updatePhysicalDamage({
          caseId,
          physicalDamageId,
          form: {
            description: toApiValue(newDescription),
            estimatedCost: toApiValue(newEstimatedCost)
          },
          onFinish: success => {
            setSubmitting(false);
            if (success) goBack();
            else {
              trackException(
                `Failed to update physical damage: ${physicalDamageId} at case: ${caseId}`
              );
            }
          }
        })
      );
    } else
      dispatch(
        uploadDescriptions({
          caseId,
          physicalDamageId,
          texts: descriptions,
          onFinish: successDescription => {
            if (estimatedCosts.length > 0)
              dispatch(
                uploadEstimatedCost({
                  caseId,
                  physicalDamageId,
                  texts: estimatedCosts,
                  onFinish: successCost => {
                    setSubmitting(false);
                    if (successDescription && successCost) goBack();
                    else {
                      trackException(
                        `Failed to add description or estimated cost to physical damage: ${physicalDamageId} at case: ${caseId}`
                      );
                    }
                  }
                })
              );
            else {
              setSubmitting(false);
              resetForm();
              if (successDescription) goBack();
              else {
                trackException(
                  `Failed to add description to physical damage: ${physicalDamageId} at case: ${caseId}`
                );
              }
            }
          }
        })
      );
  };

  const getConstantValues = arr => (data && !isDraft ? data[arr] : []);

  return (
    <EditMediaForm
      caseId={caseId}
      itemId={physicalDamageId}
      page={capturePages.PHYSICAL_DAMAGE}
      title={`${getText("physicalDamagePage.title")} ${displayNumber}`}
      exitPromptText={getText("editPhysicalDamagePage.exitPrompt")}
      fields={[
        {
          name: "descriptions",
          title: getText("editPhysicalDamagePage.provideDescriptionTitle"),
          label: getText("editPhysicalDamagePage.damageDescriptionTitle"),
          placeholder: getText("editPhysicalDamagePage.provideDescriptionTitle"),
          addButtonText: getText(
            "editInjuryPage.descriptionAddButton"
          ).toUpperCase(),
          constantValues: getConstantValues("descriptions")
        },
        {
          name: "estimatedCosts",
          title: getText("editPhysicalDamagePage.costEstimateInputPlaceholder"),
          label: getText("editPhysicalDamagePage.costEstimateInputTitle"),
          placeholder: getText(
            "editPhysicalDamagePage.costEstimateInputPlaceholder"
          ),
          addButtonText: getText(
            "editInjuryPage.descriptionAddButton"
          ).toUpperCase(),
          constantValues: getConstantValues("estimatedCosts")
        }
      ]}
      images={data.images}
      imageHeaders={imageHeaders}
      loadingHeaders={loadingHeaders}
      isDeletingImage={isDeletingImage}
      isDraft={isDraft}
      initialValues={loadInitialValues(data, isDraft)}
      onSubmit={onSubmit}
      detailRoute={getRoute(urls.physicalDamageDetail, {
        caseId,
        physicalDamageId
      })}
    />
  );
};
